import { supabase } from './supabase'
import * as XLSX from 'xlsx'

/**
 * Generate backup data for organization
 */
export const generateBackupData = async (organizationId, includeAttachments = true) => {
  try {
    // Fetch all organization data
    const [
      { data: clients },
      { data: medicalHistories },
      { data: examinations },
      { data: carePlans },
      { data: therapies },
      { data: sessions },
      { data: progressNotes },
      { data: nutritionTherapy },
      { data: carePlanProgress },
      { data: carePlanOutcomes },
    ] = await Promise.all([
      supabase.from('clients').select('*').eq('organization_id', organizationId),
      supabase.from('medical_histories').select('*').eq('organization_id', organizationId),
      supabase.from('examinations').select('*').eq('organization_id', organizationId),
      supabase.from('care_plans').select('*').eq('organization_id', organizationId),
      supabase.from('therapies').select('*').eq('organization_id', organizationId),
      supabase.from('sessions').select('*').eq('organization_id', organizationId),
      supabase.from('progress_notes').select('*').eq('organization_id', organizationId),
      supabase.from('nutrition_therapy').select('*').eq('organization_id', organizationId),
      supabase.from('care_plan_progress').select('*').eq('organization_id', organizationId),
      supabase.from('care_plan_outcomes').select('*').eq('organization_id', organizationId),
    ])

    const backupData = {
      metadata: {
        organization_id: organizationId,
        backup_date: new Date().toISOString(),
        version: '1.0',
        include_attachments: includeAttachments,
      },
      data: {
        clients: clients?.data || [],
        medical_histories: medicalHistories?.data || [],
        examinations: examinations?.data || [],
        care_plans: carePlans?.data || [],
        therapies: therapies?.data || [],
        sessions: sessions?.data || [],
        progress_notes: progressNotes?.data || [],
        nutrition_therapy: nutritionTherapy?.data || [],
        care_plan_progress: carePlanProgress?.data || [],
        care_plan_outcomes: carePlanOutcomes?.data || [],
      },
    }

    return backupData
  } catch (error) {
    console.error('Error generating backup data:', error)
    throw error
  }
}

/**
 * Download backup as JSON file
 */
export const downloadBackupJSON = async (organizationId, includeAttachments = true) => {
  try {
    const backupData = await generateBackupData(organizationId, includeAttachments)
    const blob = new Blob([JSON.stringify(backupData, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `backup-${organizationId}-${Date.now()}.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)

    // Log backup record
    await supabase.from('backup_records').insert({
      organization_id: organizationId,
      backup_type: 'full',
      backup_size_bytes: blob.size,
      status: 'completed',
      completed_at: new Date().toISOString(),
      storage_location: 'local_download',
    })

    return { success: true, size: blob.size }
  } catch (error) {
    console.error('Error downloading backup:', error)
    throw error
  }
}

/**
 * Download backup as Excel file
 */
export const downloadBackupExcel = async (organizationId, includeAttachments = true) => {
  try {
    const backupData = await generateBackupData(organizationId, includeAttachments)
    const workbook = XLSX.utils.book_new()

    // Create sheets for each data type
    Object.entries(backupData.data).forEach(([sheetName, data]) => {
      if (data && data.length > 0) {
        const worksheet = XLSX.utils.json_to_sheet(data)
        XLSX.utils.book_append_sheet(workbook, worksheet, sheetName)
      }
    })

    // Add metadata sheet
    const metadataSheet = XLSX.utils.json_to_sheet([backupData.metadata])
    XLSX.utils.book_append_sheet(workbook, metadataSheet, 'Metadata')

    // Generate Excel file
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `backup-${organizationId}-${Date.now()}.xlsx`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)

    // Log backup record
    await supabase.from('backup_records').insert({
      organization_id: organizationId,
      backup_type: 'full',
      backup_size_bytes: blob.size,
      status: 'completed',
      completed_at: new Date().toISOString(),
      storage_location: 'local_download',
    })

    return { success: true, size: blob.size }
  } catch (error) {
    console.error('Error downloading Excel backup:', error)
    throw error
  }
}

/**
 * Upload backup to Google Drive
 */
export const uploadBackupToGoogleDrive = async (organizationId, includeAttachments = true) => {
  try {
    // This would integrate with Google Drive API
    // For now, we'll return a structure for integration
    
    const backupData = await generateBackupData(organizationId, includeAttachments)
    const jsonString = JSON.stringify(backupData, null, 2)
    const blob = new Blob([jsonString], { type: 'application/json' })

    // Google Drive API integration would go here
    // For now, we'll create a placeholder
    const driveFileId = await uploadToGoogleDrive(blob, `backup-${organizationId}-${Date.now()}.json`)

    // Log backup record
    await supabase.from('backup_records').insert({
      organization_id: organizationId,
      backup_type: 'full',
      backup_size_bytes: blob.size,
      status: 'completed',
      completed_at: new Date().toISOString(),
      storage_location: `google_drive:${driveFileId}`,
    })

    return { success: true, fileId: driveFileId, size: blob.size }
  } catch (error) {
    console.error('Error uploading to Google Drive:', error)
    throw error
  }
}

/**
 * Upload to Google Drive (placeholder - needs Google Drive API setup)
 */
const uploadToGoogleDrive = async (blob, filename) => {
  // This would use Google Drive API
  // Requires: Google Drive API client library and OAuth setup
  // For now, return a placeholder
  throw new Error('Google Drive integration requires API setup. See BACKUP_SERVICE_SETUP.md')
}

/**
 * Upload backup to Dropbox
 */
export const uploadBackupToDropbox = async (organizationId, includeAttachments = true) => {
  try {
    const backupData = await generateBackupData(organizationId, includeAttachments)
    const jsonString = JSON.stringify(backupData, null, 2)
    const blob = new Blob([jsonString], { type: 'application/json' })

    // Dropbox API integration would go here
    const dropboxPath = await uploadToDropbox(blob, `/backups/backup-${organizationId}-${Date.now()}.json`)

    // Log backup record
    await supabase.from('backup_records').insert({
      organization_id: organizationId,
      backup_type: 'full',
      backup_size_bytes: blob.size,
      status: 'completed',
      completed_at: new Date().toISOString(),
      storage_location: `dropbox:${dropboxPath}`,
    })

    return { success: true, path: dropboxPath, size: blob.size }
  } catch (error) {
    console.error('Error uploading to Dropbox:', error)
    throw error
  }
}

/**
 * Upload to Dropbox (placeholder - needs Dropbox API setup)
 */
const uploadToDropbox = async (blob, path) => {
  // This would use Dropbox API
  // Requires: Dropbox API client library and OAuth setup
  throw new Error('Dropbox integration requires API setup. See BACKUP_SERVICE_SETUP.md')
}

/**
 * Upload backup to AWS S3
 */
export const uploadBackupToS3 = async (organizationId, includeAttachments = true) => {
  try {
    const backupData = await generateBackupData(organizationId, includeAttachments)
    const jsonString = JSON.stringify(backupData, null, 2)
    const blob = new Blob([jsonString], { type: 'application/json' })

    // AWS S3 integration would go here
    const s3Key = await uploadToS3(blob, `backups/${organizationId}/backup-${Date.now()}.json`)

    // Log backup record
    await supabase.from('backup_records').insert({
      organization_id: organizationId,
      backup_type: 'full',
      backup_size_bytes: blob.size,
      status: 'completed',
      completed_at: new Date().toISOString(),
      storage_location: `s3:${s3Key}`,
    })

    return { success: true, key: s3Key, size: blob.size }
  } catch (error) {
    console.error('Error uploading to S3:', error)
    throw error
  }
}

/**
 * Upload to S3 (placeholder - needs AWS SDK setup)
 */
const uploadToS3 = async (blob, key) => {
  // This would use AWS SDK
  // Requires: AWS credentials and S3 bucket configuration
  throw new Error('AWS S3 integration requires SDK setup. See BACKUP_SERVICE_SETUP.md')
}

/**
 * Get backup records for organization
 */
export const getBackupRecords = async (organizationId, limit = 50) => {
  try {
    const { data, error } = await supabase
      .from('backup_records')
      .select('*')
      .eq('organization_id', organizationId)
      .order('created_at', { ascending: false })
      .limit(limit)

    if (error) throw error
    return { success: true, data: data || [] }
  } catch (error) {
    console.error('Error getting backup records:', error)
    return { success: false, error: error.message, data: [] }
  }
}

/**
 * Restore from backup
 */
export const restoreFromBackup = async (organizationId, backupData) => {
  try {
    // Validate backup structure
    if (!backupData.metadata || !backupData.data) {
      throw new Error('Invalid backup format')
    }

    // Restore data (in transaction if possible)
    const restorePromises = []

    if (backupData.data.clients) {
      restorePromises.push(
        supabase.from('clients').upsert(
          backupData.data.clients.map((client) => ({
            ...client,
            organization_id: organizationId,
          })),
          { onConflict: 'id' }
        )
      )
    }

    // Add other tables similarly...

    await Promise.all(restorePromises)

    return { success: true }
  } catch (error) {
    console.error('Error restoring backup:', error)
    throw error
  }
}

